/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import * as EDSWeb from "@eds-web/react/icons";
import { ComponentProps, ReactNode } from "react";

import { EdsIconType, IconColor, EdsGradientType, IconSize, DeprecatedEdsIconType, EdsIconColor } from "./EdsIcon.type";
import EdsIconGradient, { tokenToGradientId } from "./EdsIcon.gradient";
import { themeVars } from "@eds-web/react/theme";
import { useIsRtl } from "@components/hooks";

type ToNumber<S> = S extends `${infer N extends number}` ? N : never;
type DimensionTokens = keyof (typeof themeVars)["size"]["dimension"];
type EdsIconSize = Exclude<ToNumber<DimensionTokens>, "px">;
type NonNullable<T> = Exclude<T, null | undefined>;

export type CosmosIconsType = EdsIconType | DeprecatedEdsIconType;
export type CosmosIconsTypeStrict = NonNullable<CosmosIconsType>;

function swapRTLIcon(icon: CosmosIconsTypeStrict): CosmosIconsTypeStrict {
	if (typeof icon !== "string") return icon;

	let newIcon;

	if (icon.includes("Left")) {
		newIcon = icon.replace(/Left/, "Right");
	} else if (icon.includes("Right")) {
		newIcon = icon.replace(/Right/, "Left");
	}

	if (newIcon) {
		const swappedKey = `${newIcon}Icon` as keyof typeof EDSWeb;
		if (swappedKey in EDSWeb) return newIcon as CosmosIconsTypeStrict;
	}
	return icon;
}

export type EdsIconProps = {
	icon?: CosmosIconsType | null;
	color?: IconColor;
	size?: IconSize;
	gradient?: EdsGradientType | null;
	className?: string | null;
};

// Note - Some cases commented out because the icon is available in EDS storybook, but not available in current version eds-web package
export const EdsIcon = ({ icon, color, size, gradient, className }: EdsIconProps): ReactNode => {
	const props: ComponentProps<typeof EDSWeb.AIIcon> = {};

	if (gradient) {
		props.style = { ...props?.style, fill: `url(#${tokenToGradientId(gradient)})` };
	}

	if (className) {
		props.className = className;
	}

	let edsSize: EdsIconSize = 24;
	if (size === "xs") {
		edsSize = 16;
	} else if (size === "sm") {
		edsSize = 20;
	} else if (size === "md") {
		edsSize = 24;
	} else if (size === "lg") {
		edsSize = 28;
	} else if (size === "xl") {
		edsSize = 40;
	} else if (size === "xxl") {
		edsSize = 80;
	}

	let edsColor: EdsIconColor;
	if (color === "contrast") {
		edsColor = "primary";
	} else {
		edsColor = color;
	}

	//flip Left and Right icons if RTL
	const isRtl = useIsRtl();
	let finalIcon = icon?.toString() as CosmosIconsType;
	if (finalIcon && isRtl) {
		finalIcon = swapRTLIcon(finalIcon);
	}

	let edsIcon;
	switch (finalIcon) {
		case "AI":
			edsIcon = <EDSWeb.AIIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "AlarmClock":
			edsIcon = <EDSWeb.AlarmClockIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Android":
			edsIcon = <EDSWeb.AndroidIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Apple":
			edsIcon = <EDSWeb.AppleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Archive":
			edsIcon = <EDSWeb.ArchiveIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowDownCircleFilled":
			edsIcon = <EDSWeb.ArrowDownCircleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowDownCircle":
			edsIcon = <EDSWeb.ArrowDownCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowDown":
			edsIcon = <EDSWeb.ArrowDownIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowDownRightArrowUpLeft":
			edsIcon = <EDSWeb.ArrowDownRightArrowUpLeftIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowDownRightToSquare":
			edsIcon = <EDSWeb.ArrowDownRightToSquareIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowDownToBracket":
			edsIcon = <EDSWeb.ArrowDownToBracketIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowLeftCircleFilled":
			edsIcon = <EDSWeb.ArrowLeftCircleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowLeftCircle":
			edsIcon = <EDSWeb.ArrowLeftCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowLeft":
			edsIcon = <EDSWeb.ArrowLeftIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowLeftToLine":
			edsIcon = <EDSWeb.ArrowLeftToLineIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowRightArrowLeftHorizontal":
			edsIcon = <EDSWeb.ArrowRightArrowLeftHorizontalIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowRightCircleFilled":
			edsIcon = <EDSWeb.ArrowRightCircleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowRightCircle":
			edsIcon = <EDSWeb.ArrowRightCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowRightFromBracket":
			edsIcon = <EDSWeb.ArrowRightFromBracketIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowRight":
			edsIcon = <EDSWeb.ArrowRightIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowRightToBracket":
			edsIcon = <EDSWeb.ArrowRightToBracketIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowRightToLine":
			edsIcon = <EDSWeb.ArrowRightToLineIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowRotateLeft":
			edsIcon = <EDSWeb.ArrowRotateLeftIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowRotateRight":
			edsIcon = <EDSWeb.ArrowRotateRightIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowRotateX":
			edsIcon = <EDSWeb.ArrowRotateXIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowRotateY":
			edsIcon = <EDSWeb.ArrowRotateYIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowTurnLeftDown":
			edsIcon = <EDSWeb.ArrowTurnLeftDownIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowTurnUpLeft":
			edsIcon = <EDSWeb.ArrowTurnUpLeftIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowTurnUpRight":
			edsIcon = <EDSWeb.ArrowTurnUpRightIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowUpArrowDownVertical":
			edsIcon = <EDSWeb.ArrowUpArrowDownVerticalIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowUpCircleFilled":
			edsIcon = <EDSWeb.ArrowUpCircleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowUpCircle":
			edsIcon = <EDSWeb.ArrowUpCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowUpFromBracket":
			edsIcon = <EDSWeb.ArrowUpFromBracketIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowUp":
			edsIcon = <EDSWeb.ArrowUpIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowUpLeftArrowDownRight":
			edsIcon = <EDSWeb.ArrowUpLeftArrowDownRightIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowUpRightFromSquare":
			edsIcon = <EDSWeb.ArrowUpRightFromSquareIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowsCrossed":
			edsIcon = <EDSWeb.ArrowsCrossedIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowsHorizontalFromCenter":
			edsIcon = <EDSWeb.ArrowsHorizontalFromCenterIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowsRotateRight":
			edsIcon = <EDSWeb.ArrowsRotateRightIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArrowsVerticalFromCenter":
			edsIcon = <EDSWeb.ArrowsVerticalFromCenterIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ArtStation":
			edsIcon = <EDSWeb.ArtStationIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "At":
			edsIcon = <EDSWeb.AtIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Backpack":
			edsIcon = <EDSWeb.BackpackIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "BackwardEndFilled":
			edsIcon = <EDSWeb.BackwardEndFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "BackwardEnd":
			edsIcon = <EDSWeb.BackwardEndIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "BackwardFilled":
			edsIcon = <EDSWeb.BackwardFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Backward":
			edsIcon = <EDSWeb.BackwardIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Ball":
			edsIcon = <EDSWeb.BallIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Bell":
			edsIcon = <EDSWeb.BellIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "BellSlash":
			edsIcon = <EDSWeb.BellSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Blender":
			edsIcon = <EDSWeb.BlenderIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "BlockQuote":
			edsIcon = <EDSWeb.BlockQuoteIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Bold":
			edsIcon = <EDSWeb.BoldIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Bolt":
			edsIcon = <EDSWeb.BoltIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "BoltSlash":
			edsIcon = <EDSWeb.BoltSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "BookmarkFilled":
			edsIcon = <EDSWeb.BookmarkFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Bookmark":
			edsIcon = <EDSWeb.BookmarkIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Box":
			edsIcon = <EDSWeb.BoxIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "BrickWall":
			edsIcon = <EDSWeb.BrickWallIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "BrowserWindowCogwheel":
			edsIcon = <EDSWeb.BrowserWindowCogwheelIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "BrowserWindow":
			edsIcon = <EDSWeb.BrowserWindowIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Brush":
			edsIcon = <EDSWeb.BrushIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Bug":
			edsIcon = <EDSWeb.BugIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Building":
			edsIcon = <EDSWeb.BuildingIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "BulletList":
			edsIcon = <EDSWeb.BulletListIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Bullhorn":
			edsIcon = <EDSWeb.BullhornIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Burger":
			edsIcon = <EDSWeb.BurgerIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Calendar":
			edsIcon = <EDSWeb.CalendarIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Camera":
			edsIcon = <EDSWeb.CameraIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "CameraSlash":
			edsIcon = <EDSWeb.CameraSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Car":
			edsIcon = <EDSWeb.CarIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ChartBarXAxis":
			edsIcon = <EDSWeb.ChartBarXAxisIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ChartLineDownTrendXYAxis":
			edsIcon = <EDSWeb.ChartLineDownTrendXYAxisIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ChartLineUpTrendXYAxis":
			edsIcon = <EDSWeb.ChartLineUpTrendXYAxisIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "CheckCircleFilled":
			edsIcon = <EDSWeb.CheckCircleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "CheckCircle":
			edsIcon = <EDSWeb.CheckCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Check":
			edsIcon = <EDSWeb.CheckIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "CheckmarkSealFilled":
			edsIcon = <EDSWeb.CheckmarkSealFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "CheckmarkSeal":
			edsIcon = <EDSWeb.CheckmarkSealIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ChevronDown":
			edsIcon = <EDSWeb.ChevronDownIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ChevronLeft":
			edsIcon = <EDSWeb.ChevronLeftIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ChevronRight":
			edsIcon = <EDSWeb.ChevronRightIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ChevronUp":
			edsIcon = <EDSWeb.ChevronUpIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "CirclesBottomLeftTopRight":
			edsIcon = <EDSWeb.CirclesBottomLeftTopRightIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Clapper":
			edsIcon = <EDSWeb.ClapperIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Clipboard":
			edsIcon = <EDSWeb.ClipboardIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Clock":
			edsIcon = <EDSWeb.ClockIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "CloudArrowDown":
			edsIcon = <EDSWeb.CloudArrowDownIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "CloudArrowUp":
			edsIcon = <EDSWeb.CloudArrowUpIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Cloud":
			edsIcon = <EDSWeb.CloudIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Code":
			edsIcon = <EDSWeb.CodeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Cog":
			edsIcon = <EDSWeb.CogIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "CombinedGeometricShapes":
			edsIcon = <EDSWeb.CombinedGeometricShapesIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Compass":
			edsIcon = <EDSWeb.CompassIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "CompileFilled":
			edsIcon = <EDSWeb.CompileFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Compile":
			edsIcon = <EDSWeb.CompileIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Compress":
			edsIcon = <EDSWeb.CompressIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ComputerNetworkFilled":
			edsIcon = <EDSWeb.ComputerNetworkFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ComputerChip":
			edsIcon = <EDSWeb.ComputerChipIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Coupon":
			edsIcon = <EDSWeb.CouponIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "CreditCard":
			edsIcon = <EDSWeb.CreditCardIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Crop":
			edsIcon = <EDSWeb.CropIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Crosshair":
			edsIcon = <EDSWeb.CrosshairIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Cube":
			edsIcon = <EDSWeb.CubeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Database":
			edsIcon = <EDSWeb.DatabaseIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "DiagonalLinesSquare":
			edsIcon = <EDSWeb.DiagonalLinesSquareIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "DirectionalPad":
			edsIcon = <EDSWeb.DirectionalPadIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Discord":
			edsIcon = <EDSWeb.DiscordIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Display":
			edsIcon = <EDSWeb.DisplayIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "DollarSignCircle":
			edsIcon = <EDSWeb.DollarSignCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "DragHandleHorizontal":
			edsIcon = <EDSWeb.DragHandleHorizontalIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "DragHandleVertical":
			edsIcon = <EDSWeb.DragHandleVerticalIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EllipsisHorizontalCircle":
			edsIcon = <EDSWeb.EllipsisHorizontalCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EllipsisHorizontal":
			edsIcon = <EDSWeb.EllipsisHorizontalIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EllipsisVerticalCircle":
			edsIcon = <EDSWeb.EllipsisVerticalCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EllipsisVertical":
			edsIcon = <EDSWeb.EllipsisVerticalIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EnvelopeFilled":
			edsIcon = <EDSWeb.EnvelopeFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Envelope":
			edsIcon = <EDSWeb.EnvelopeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EnvelopeOpen":
			edsIcon = <EDSWeb.EnvelopeOpenIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EnvelopeXMark":
			edsIcon = <EDSWeb.EnvelopeXMarkIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EpicGames":
			edsIcon = <EDSWeb.EpicGamesIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Epic":
			edsIcon = <EDSWeb.EpicIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EpicRewardsFilled":
			edsIcon = <EDSWeb.EpicRewardsFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EpicRewards":
			edsIcon = <EDSWeb.EpicRewardsIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Eraser":
			edsIcon = <EDSWeb.EraserIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EuroSignCircle":
			edsIcon = <EDSWeb.EuroSignCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ExclamationCircleFilled":
			edsIcon = <EDSWeb.ExclamationCircleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ExclamationCircle":
			edsIcon = <EDSWeb.ExclamationCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ExclamationTriangleFilled":
			edsIcon = <EDSWeb.ExclamationTriangleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ExclamationTriangle":
			edsIcon = <EDSWeb.ExclamationTriangleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Expand":
			edsIcon = <EDSWeb.ExpandIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ExternalDrive":
			edsIcon = <EDSWeb.ExternalDriveIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EyeDropper":
			edsIcon = <EDSWeb.EyeDropperIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Eye":
			edsIcon = <EDSWeb.EyeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "EyeSlash":
			edsIcon = <EDSWeb.EyeSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Fab":
			edsIcon = <EDSWeb.FabIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Facebook":
			edsIcon = <EDSWeb.FacebookIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FallGuys":
			edsIcon = <EDSWeb.FallGuysIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Figma":
			edsIcon = <EDSWeb.FigmaIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FileCogwheel":
			edsIcon = <EDSWeb.FileCogwheelIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "File":
			edsIcon = <EDSWeb.FileIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FileLines":
			edsIcon = <EDSWeb.FileLinesIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FileMagnifier":
			edsIcon = <EDSWeb.FileMagnifierIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FilePen":
			edsIcon = <EDSWeb.FilePenIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FileZipper":
			edsIcon = <EDSWeb.FileZipperIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Film":
			edsIcon = <EDSWeb.FilmIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FlagFilled":
			edsIcon = <EDSWeb.FlagFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Flag":
			edsIcon = <EDSWeb.FlagIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Flame":
			edsIcon = <EDSWeb.FlameIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FloppyDisk":
			edsIcon = <EDSWeb.FloppyDiskIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Flower":
			edsIcon = <EDSWeb.FlowerIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FolderClosed":
			edsIcon = <EDSWeb.FolderClosedIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FolderCogwheel":
			edsIcon = <EDSWeb.FolderCogwheelIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Folder":
			edsIcon = <EDSWeb.FolderIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FolderMagnifier":
			edsIcon = <EDSWeb.FolderMagnifierIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FolderOpen":
			edsIcon = <EDSWeb.FolderOpenIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FolderPen":
			edsIcon = <EDSWeb.FolderPenIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FolderUser":
			edsIcon = <EDSWeb.FolderUserIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FormattingSlash":
			edsIcon = <EDSWeb.FormattingSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Fortnite":
			edsIcon = <EDSWeb.FortniteIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FortniteCreative":
			edsIcon = <EDSWeb.FortniteCreativeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ForwardEndFilled":
			edsIcon = <EDSWeb.ForwardEndFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ForwardEnd":
			edsIcon = <EDSWeb.ForwardEndIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ForwardFilled":
			edsIcon = <EDSWeb.ForwardFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Forward":
			edsIcon = <EDSWeb.ForwardIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FourLinesHorizontalAlignCenter":
			edsIcon = <EDSWeb.FourLinesHorizontalAlignCenterIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FourLinesHorizontalAlignLeft":
			edsIcon = <EDSWeb.FourLinesHorizontalAlignLeftIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FourLinesHorizontalAlignRight":
			edsIcon = <EDSWeb.FourLinesHorizontalAlignRightIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "FourLinesHorizontal":
			edsIcon = <EDSWeb.FourLinesHorizontalIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Funnel":
			edsIcon = <EDSWeb.FunnelIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "GIF":
			edsIcon = <EDSWeb.GIFIcon color={edsColor} size={edsSize} {...props} />;
			break;

		//deprecated
		case "GamePadTrendUp":
			edsIcon = <EDSWeb.GamepadTrendUpIcon color={edsColor} size={edsSize} {...props} />;
			break;
		case "GamepadTrendUp":
			edsIcon = <EDSWeb.GamepadTrendUpIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Gamepad":
			edsIcon = <EDSWeb.GamepadIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Ghost":
			edsIcon = <EDSWeb.GhostIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "GiftCard":
			edsIcon = <EDSWeb.GiftCardIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Gift":
			edsIcon = <EDSWeb.GiftIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Github":
			edsIcon = <EDSWeb.GithubIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Glasses":
			edsIcon = <EDSWeb.GlassesIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Globe":
			edsIcon = <EDSWeb.GlobeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "GlobeLocation":
			edsIcon = <EDSWeb.GlobeLocationIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Google":
			edsIcon = <EDSWeb.GoogleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "GraduationCap":
			edsIcon = <EDSWeb.GraduationCapIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Hashtag":
			edsIcon = <EDSWeb.HashtagIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Heading":
			edsIcon = <EDSWeb.HeadingIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Headphones":
			edsIcon = <EDSWeb.HeadphonesIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "HeartFilled":
			edsIcon = <EDSWeb.HeartFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Heart":
			edsIcon = <EDSWeb.HeartIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Hook":
			edsIcon = <EDSWeb.HookIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Hourglass":
			edsIcon = <EDSWeb.HourglassIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "HouseFilled":
			edsIcon = <EDSWeb.HouseFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "House":
			edsIcon = <EDSWeb.HouseIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ImageCarousel":
			edsIcon = <EDSWeb.ImageCarouselIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Image":
			edsIcon = <EDSWeb.ImageIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ImageLandscape":
			edsIcon = <EDSWeb.ImageLandscapeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ImageLandscapeMagnifier":
			edsIcon = <EDSWeb.ImageLandscapeMagnifierIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ImagePanorama":
			edsIcon = <EDSWeb.ImagePanoramaIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ImageSlash":
			edsIcon = <EDSWeb.ImageSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		//deprecated
		case "ImageSquare":
			edsIcon = <EDSWeb.SquareIcon color={edsColor} size={edsSize} {...props} />;
			break;
		case "Square":
			edsIcon = <EDSWeb.SquareIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ImagesStacked":
			edsIcon = <EDSWeb.ImagesStackedIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Infinity":
			edsIcon = <EDSWeb.InfinityIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "InfoCircleFilled":
			edsIcon = <EDSWeb.InfoCircleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "InfoCircle":
			edsIcon = <EDSWeb.InfoCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Instagram":
			edsIcon = <EDSWeb.InstagramIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Italic":
			edsIcon = <EDSWeb.ItalicIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "JPG":
			edsIcon = <EDSWeb.JPGIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Key":
			edsIcon = <EDSWeb.KeyIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "KidsWebServices":
			edsIcon = <EDSWeb.KidsWebServicesIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Lamp":
			edsIcon = <EDSWeb.LampIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Language":
			edsIcon = <EDSWeb.LanguageIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Laptop":
			edsIcon = <EDSWeb.LaptopIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Layout1":
			edsIcon = <EDSWeb.Layout1Icon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Layout2":
			edsIcon = <EDSWeb.Layout2Icon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Layout3":
			edsIcon = <EDSWeb.Layout3Icon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Layout4":
			edsIcon = <EDSWeb.Layout4Icon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Leaf":
			edsIcon = <EDSWeb.LeafIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LegalHammer":
			edsIcon = <EDSWeb.LegalHammerIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LegalHammerSlash":
			edsIcon = <EDSWeb.LegalHammerSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LegoBrick":
			edsIcon = <EDSWeb.LegoBrickIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LegoHead":
			edsIcon = <EDSWeb.LegoHeadIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LifePreserver":
			edsIcon = <EDSWeb.LifePreserverIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Lightbulb":
			edsIcon = <EDSWeb.LightbulbIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LineBarsVertical":
			edsIcon = <EDSWeb.LineBarsVerticalIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LinesHorizontal":
			edsIcon = <EDSWeb.LinesHorizontalIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LinesSquareStackFilled":
			edsIcon = <EDSWeb.LinesSquareStackFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LinesSquareStack":
			edsIcon = <EDSWeb.LinesSquareStackIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LinkBroken":
			edsIcon = <EDSWeb.LinkBrokenIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Link":
			edsIcon = <EDSWeb.LinkIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LinkedIn":
			edsIcon = <EDSWeb.LinkedInIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Linux":
			edsIcon = <EDSWeb.LinuxIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LocationDrop":
			edsIcon = <EDSWeb.LocationDropIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Lock":
			edsIcon = <EDSWeb.LockIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "LockOpen":
			edsIcon = <EDSWeb.LockOpenIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "MacOS":
			edsIcon = <EDSWeb.MacOSIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Magazine":
			edsIcon = <EDSWeb.MagazineIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Magnifier":
			edsIcon = <EDSWeb.MagnifierIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "MagnifierMinus":
			edsIcon = <EDSWeb.MagnifierMinusIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "MagnifierPlus":
			edsIcon = <EDSWeb.MagnifierPlusIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Medal1":
			edsIcon = <EDSWeb.Medal1Icon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Medal2":
			edsIcon = <EDSWeb.Medal2Icon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Medal3":
			edsIcon = <EDSWeb.Medal3Icon color={edsColor} size={edsSize} {...props} />;
			break;

		case "MedalStar":
			edsIcon = <EDSWeb.MedalStarIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Metahuman":
			edsIcon = <EDSWeb.MetahumanIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "MilitaryBadge":
			edsIcon = <EDSWeb.MilitaryBadgeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "MinusCircleFilled":
			edsIcon = <EDSWeb.MinusCircleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "MinusCircle":
			edsIcon = <EDSWeb.MinusCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Minus":
			edsIcon = <EDSWeb.MinusIcon color={edsColor} size={edsSize} {...props} />;
			break;

		//deprecated
		case "MonitorXMark":
			edsIcon = <EDSWeb.XMarkIcon color={edsColor} size={edsSize} {...props} />;
			break;
		case "XMark":
			edsIcon = <EDSWeb.XMarkIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "MultipleShapes":
			edsIcon = <EDSWeb.MultipleShapesIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "MusicNote":
			edsIcon = <EDSWeb.MusicNoteIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Naver":
			edsIcon = <EDSWeb.NaverIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Newspaper":
			edsIcon = <EDSWeb.NewspaperIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "NintendoSwitch":
			edsIcon = <EDSWeb.NintendoSwitchIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Node":
			edsIcon = <EDSWeb.NodeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "NotePenFilled":
			edsIcon = <EDSWeb.NotePenFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "NotePen":
			edsIcon = <EDSWeb.NotePenIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Note":
			edsIcon = <EDSWeb.NoteIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "NumberedList":
			edsIcon = <EDSWeb.NumberedListIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Off":
			edsIcon = <EDSWeb.OffIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PNG":
			edsIcon = <EDSWeb.PNGIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PRO":
			edsIcon = <EDSWeb.PROIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PaintBrush":
			edsIcon = <EDSWeb.PaintBrushIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Palette":
			edsIcon = <EDSWeb.PaletteIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PalmTree":
			edsIcon = <EDSWeb.PalmTreeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PaperPlane":
			edsIcon = <EDSWeb.PaperPlaneIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Paragraph":
			edsIcon = <EDSWeb.ParagraphIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ParentChild":
			edsIcon = <EDSWeb.ParentChildIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PauseFilled":
			edsIcon = <EDSWeb.PauseFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Pause":
			edsIcon = <EDSWeb.PauseIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Paw":
			edsIcon = <EDSWeb.PawIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Pencil":
			edsIcon = <EDSWeb.PencilIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PencilSlash":
			edsIcon = <EDSWeb.PencilSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Percent":
			edsIcon = <EDSWeb.PercentIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PercentSeal":
			edsIcon = <EDSWeb.PercentSealIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Phone":
			edsIcon = <EDSWeb.PhoneIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Pinterest":
			edsIcon = <EDSWeb.PinterestIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PlayFilled":
			edsIcon = <EDSWeb.PlayFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Play":
			edsIcon = <EDSWeb.PlayIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PlayStation":
			edsIcon = <EDSWeb.PlayStationIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PlusCircleFilled":
			edsIcon = <EDSWeb.PlusCircleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PlusCircle":
			edsIcon = <EDSWeb.PlusCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Plus":
			edsIcon = <EDSWeb.PlusIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Postparty":
			edsIcon = <EDSWeb.PostpartyIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PowerCord":
			edsIcon = <EDSWeb.PowerCordIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "PromptInfo":
			edsIcon = <EDSWeb.PromptInfoIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "QuestionCircleFilled":
			edsIcon = <EDSWeb.QuestionCircleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "QuestionCircle":
			edsIcon = <EDSWeb.QuestionCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Quixel":
			edsIcon = <EDSWeb.QuixelIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "RealityCapture":
			edsIcon = <EDSWeb.RealityCaptureIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "RealityScan":
			edsIcon = <EDSWeb.RealityScanIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Receipt":
			edsIcon = <EDSWeb.ReceiptIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Reddit":
			edsIcon = <EDSWeb.RedditIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Resolution16K":
			edsIcon = <EDSWeb.Resolution16KIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Resolution4K":
			edsIcon = <EDSWeb.Resolution4KIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Resolution8K":
			edsIcon = <EDSWeb.Resolution8KIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "RobotXMarks":
			edsIcon = <EDSWeb.RobotXMarksIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Rocket":
			edsIcon = <EDSWeb.RocketIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "RocketLeague":
			edsIcon = <EDSWeb.RocketLeagueIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "RocketLeagueSideswipe":
			edsIcon = <EDSWeb.RocketLeagueSideswipeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "RouteStart":
			edsIcon = <EDSWeb.RouteStartIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "SVG":
			edsIcon = <EDSWeb.SVGIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Scroll":
			edsIcon = <EDSWeb.ScrollIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShieldCheckFilled":
			edsIcon = <EDSWeb.ShieldCheckFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShieldCheck":
			edsIcon = <EDSWeb.ShieldCheckIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Shield":
			edsIcon = <EDSWeb.ShieldIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShieldLock":
			edsIcon = <EDSWeb.ShieldLockIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShieldPlus":
			edsIcon = <EDSWeb.ShieldPlusIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShieldPlusFilled":
			edsIcon = <EDSWeb.ShieldPlusFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShieldSlash":
			edsIcon = <EDSWeb.ShieldSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShieldXMark":
			edsIcon = <EDSWeb.ShieldXMarkIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShoppingBagClock":
			edsIcon = <EDSWeb.ShoppingBagClockIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShoppingBag":
			edsIcon = <EDSWeb.ShoppingBagIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShoppingCartFilled":
			edsIcon = <EDSWeb.ShoppingCartFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShoppingCart":
			edsIcon = <EDSWeb.ShoppingCartIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ShoppingCartTrendUp":
			edsIcon = <EDSWeb.ShoppingCartTrendUpIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Signal":
			edsIcon = <EDSWeb.SignalIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Sketchfab":
			edsIcon = <EDSWeb.SketchfabIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Skyscraper":
			edsIcon = <EDSWeb.SkyscraperIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Slack":
			edsIcon = <EDSWeb.SlackIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "SlashCircle":
			edsIcon = <EDSWeb.SlashCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "SlidersHorizontal":
			edsIcon = <EDSWeb.SlidersHorizontalIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Snapchat":
			edsIcon = <EDSWeb.SnapchatIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Snowflake":
			edsIcon = <EDSWeb.SnowflakeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Sparkle":
			edsIcon = <EDSWeb.SparkleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "SpeakerSlash":
			edsIcon = <EDSWeb.SpeakerSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "SquareGrid2x2":
			edsIcon = <EDSWeb.SquareGrid2x2Icon color={edsColor} size={edsSize} {...props} />;
			break;

		case "SquareGridPlus":
			edsIcon = <EDSWeb.SquareGridPlusIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "SquareSplitVertically":
			edsIcon = <EDSWeb.SquareSplitVerticallyIcon color={edsColor} size={edsSize} {...props} />;
			break;

		//deprecated
		case "Stack":
			edsIcon = <EDSWeb.Stack1Icon color={edsColor} size={edsSize} {...props} />;
			break;
		case "Stack1":
			edsIcon = <EDSWeb.Stack1Icon color={edsColor} size={edsSize} {...props} />;
			break;

		case "StackedSquares":
			edsIcon = <EDSWeb.StackedSquaresIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "StarCircle":
			edsIcon = <EDSWeb.StarCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "StarFilled":
			edsIcon = <EDSWeb.StarFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Star":
			edsIcon = <EDSWeb.StarIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Steam":
			edsIcon = <EDSWeb.SteamIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "SterlingSignCircle":
			edsIcon = <EDSWeb.SterlingSignCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Sticker":
			edsIcon = <EDSWeb.StickerIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "StopFilled":
			edsIcon = <EDSWeb.StopFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Stop":
			edsIcon = <EDSWeb.StopIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Store":
			edsIcon = <EDSWeb.StoreIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "StrikeThrough":
			edsIcon = <EDSWeb.StrikeThroughIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Student":
			edsIcon = <EDSWeb.StudentIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Sun":
			edsIcon = <EDSWeb.SunIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "SunbedUmbrella":
			edsIcon = <EDSWeb.SunbedUmbrellaIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "TShirt":
			edsIcon = <EDSWeb.TShirtIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Tablet":
			edsIcon = <EDSWeb.TabletIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Tag":
			edsIcon = <EDSWeb.TagIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "TalkBubbleDots":
			edsIcon = <EDSWeb.TalkBubbleDotsIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "TalkBubbleExclamation":
			edsIcon = <EDSWeb.TalkBubbleExclamationIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "TalkBubble":
			edsIcon = <EDSWeb.TalkBubbleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "TalkBubbleSlash":
			edsIcon = <EDSWeb.TalkBubbleSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "TalkBubbles":
			edsIcon = <EDSWeb.TalkBubblesIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "TargetArrow":
			edsIcon = <EDSWeb.TargetArrowIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "TargetCursor":
			edsIcon = <EDSWeb.TargetCursorIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Template":
			edsIcon = <EDSWeb.TemplateIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Text":
			edsIcon = <EDSWeb.TextIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ThreeLateral":
			edsIcon = <EDSWeb.ThreeLateralIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ThreeLinesHorizontalDecrease":
			edsIcon = <EDSWeb.ThreeLinesHorizontalDecreaseIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ThumbDownFilled":
			edsIcon = <EDSWeb.ThumbDownFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ThumbDown":
			edsIcon = <EDSWeb.ThumbDownIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ThumbUpFilled":
			edsIcon = <EDSWeb.ThumbUpFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ThumbUp":
			edsIcon = <EDSWeb.ThumbUpIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "TikTok":
			edsIcon = <EDSWeb.TikTokIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Timer":
			edsIcon = <EDSWeb.TimerIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Trash":
			edsIcon = <EDSWeb.TrashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Tree":
			edsIcon = <EDSWeb.TreeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Trophy":
			edsIcon = <EDSWeb.TrophyIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Twinmotion":
			edsIcon = <EDSWeb.TwinmotionIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Twitch":
			edsIcon = <EDSWeb.TwitchIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "TwitterX":
			edsIcon = <EDSWeb.TwitterXIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Underline":
			edsIcon = <EDSWeb.UnderlineIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Unity":
			edsIcon = <EDSWeb.UnityIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "UnrealEngine":
			edsIcon = <EDSWeb.UnrealEngineIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Uplay":
			edsIcon = <EDSWeb.UplayIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "UserCheck":
			edsIcon = <EDSWeb.UserCheckIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "UserCogwheel":
			edsIcon = <EDSWeb.UserCogwheelIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "UserGroup":
			edsIcon = <EDSWeb.UserGroupIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "User":
			edsIcon = <EDSWeb.UserIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "UserMinus":
			edsIcon = <EDSWeb.UserMinusIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "UserPlus":
			edsIcon = <EDSWeb.UserPlusIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "UserXMark":
			edsIcon = <EDSWeb.UserXMarkIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Users":
			edsIcon = <EDSWeb.UsersIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Verse":
			edsIcon = <EDSWeb.VerseIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Video":
			edsIcon = <EDSWeb.VideoIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "VideoSlash":
			edsIcon = <EDSWeb.VideoSlashIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "View360Degree":
			edsIcon = <EDSWeb.View360DegreeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "VK":
			edsIcon = <EDSWeb.VKIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "VRay":
			edsIcon = <EDSWeb.VRayIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Wallet":
			edsIcon = <EDSWeb.WalletIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Waveform":
			edsIcon = <EDSWeb.WaveformIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "WeGame":
			edsIcon = <EDSWeb.WeGameIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Weibo":
			edsIcon = <EDSWeb.WeiboIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Windows":
			edsIcon = <EDSWeb.WindowsIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Wrench":
			edsIcon = <EDSWeb.WrenchIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "XMarkCircleFilled":
			edsIcon = <EDSWeb.XMarkCircleFilledIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "XMarkCircle":
			edsIcon = <EDSWeb.XMarkCircleIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "Xbox":
			edsIcon = <EDSWeb.XboxIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "YouTube":
			edsIcon = <EDSWeb.YouTubeIcon color={edsColor} size={edsSize} {...props} />;
			break;

		case "ZBrush":
			edsIcon = <EDSWeb.ZBrushIcon color={edsColor} size={edsSize} {...props} />;
			break;

		default:
			//const exhaustiveCheck: undefined = finalIcon;
			break;
	}

	if (!edsIcon) return undefined;
	if (!gradient) return edsIcon;

	return (
		<>
			<EdsIconGradient token={gradient} />
			{edsIcon}
		</>
	);
};
